import { useCallback, useEffect, useState } from 'react'

import { gnarSpec } from '@lib/tracking/gnar'
import { Cookie, getCookie } from '@lib/utils/cookies'
import { waitGnar } from '@lib/utils/promise'

import { getConfig } from '@config/index'

import { HOMEPAGE_SLUG } from '@constants/slugs'

const config = getConfig()
const shMailUrl = config.paths.mail.basePath

const pageSlugsWithMailRedirect = new Set([HOMEPAGE_SLUG])

const isShMailInstalled = () =>
  document?.documentElement?.classList?.contains('superhumanIsInstalled')
const hasVisitedATC = () => !!getCookie(Cookie.AtcVisited)
const hasRedirectDisabled = () => {
  const urlParams = new URLSearchParams(window?.location?.search ?? '')
  return urlParams.get('redirect') === '0'
}

interface MailRedirectProps {
  shouldRedirectToMail: boolean
  redirectToMail: () => Promise<void>
}

export const useMailRedirect = (pageSlug: string, pageId: string): MailRedirectProps => {
  const isPageWithMailRedirect = pageSlugsWithMailRedirect.has(pageSlug)

  const [shouldRedirectToMail, setShouldRedirectToMail] = useState<boolean | undefined>(
    isPageWithMailRedirect ? undefined : false,
  )

  useEffect(() => {
    if (isPageWithMailRedirect) {
      if (isShMailInstalled() && !hasVisitedATC() && !hasRedirectDisabled()) {
        setShouldRedirectToMail(true)
      } else {
        setShouldRedirectToMail(false)
      }
    }
  }, [])

  const redirectToMail = useCallback(async () => {
    await waitGnar(
      gnarSpec.pageRedirect({
        pageId,
        pageSlug,
        redirectUrl: shMailUrl,
        referrer: document.referrer,
        url: window.location.href,
      }),
    )
    window.location.href = shMailUrl
  }, [pageId, pageSlug])

  return {
    redirectToMail,
    shouldRedirectToMail: shouldRedirectToMail ?? false,
  }
}
